import {
    collection,
    deleteField,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {db, functions} from './firebase';
import type {Bug, BugCandidate, BugStatus} from '../types/bug';

const BUGS_COLLECTION = 'bugs';

export interface PromoteResult {
    bugId: string;
    /** true quand le candidat a rejoint un bug existant au lieu d'en creer un. */
    merged: boolean;
    linkedFeedbacks: number;
}

/**
 * Promeut un candidat en entite bug persistee.
 *
 * La deduplication sur l'issue Sentry est faite cote serveur : promouvoir deux
 * fois le meme candidat rattache les feedbacks au bug existant plutot que de
 * creer un doublon.
 */
export async function promoteCandidate(candidate: BugCandidate): Promise<PromoteResult> {
    const fn = httpsCallable<{ candidate: BugCandidate }, PromoteResult>(
        functions,
        'createBugFromCandidate',
    );
    const result = await fn({candidate});
    return result.data;
}

/**
 * likely_real  : au moins une preuve independante (Sentry ou corroboration)
 * unsupported  : un seul signalement, aucune trace nulle part
 * inconclusive : rien de tranchant dans un sens ou dans l'autre
 */
export type VerificationVerdict = 'likely_real' | 'unsupported' | 'inconclusive';

export interface SentryIssueRef {
    shortId: string;
    title: string;
    url: string;
    status: string;
    eventCount: number;
    userCount: number;
}

export interface VerificationCheck {
    feedbackId: string;
    platform: string | null;
    appVersion: string | null;
    sentryIssues: SentryIssueRef[];
    accountState: { progressDocs: number | null; purchaseDocs: number | null };
    similar: { feedbackId: string; confidence: number; matchedTopic: string }[];
}

export interface VerificationResult {
    verdict: VerificationVerdict;
    /** Le raisonnement, en clair. Le verdict sans les raisons n'est pas
     *  verifiable, et une verification non verifiable ne vaut rien. */
    reasons: string[];
    checks: VerificationCheck[];
}

/**
 * Pose le tag de verification sur les signalements, avec sa provenance.
 *
 * `verified` n'est accepte par le serveur que sur un verdict concluant, et
 * `not_reproduced` uniquement en source 'manual' : ce tag atteste une
 * tentative de reproduction, qu'aucune regle ne peut constater a la place
 * d'un humain.
 */
export async function applyVerificationTag(params: {
    feedbackIds: string[];
    tag: 'verified' | 'not_reproduced';
    verdict: VerificationVerdict;
    reasons: string[];
    source: 'assisted' | 'manual';
}): Promise<{ tagged: number; tag: string }> {
    const fn = httpsCallable<typeof params, { tagged: number; tag: string }>(
        functions,
        'applyVerificationTag',
    );
    const result = await fn(params);
    return result.data;
}

export async function verifyCandidate(feedbackIds: string[]): Promise<VerificationResult> {
    const fn = httpsCallable<{ feedbackIds: string[] }, VerificationResult>(
        functions,
        'verifyBugCandidate',
    );
    const result = await fn({feedbackIds});
    return result.data;
}

export interface CommitCandidate {
    hash: string;
    shortHash: string;
    message: string;
    date: string;
    url: string;
    confidence: 'high' | 'medium' | 'low';
    why: string;
}

export interface FixCommitSearch {
    /** 'exact' : trouve en citant la cle Sentry, sans modele.
     *  'semantic' : rapproche par un modele, a valider. */
    mode: 'exact' | 'semantic' | 'none';
    candidates: CommitCandidate[];
    scanned: number;
}

export interface FixVersionResolution {
    /** null quand aucun tag publié ne contient encore le commit : le correctif
     *  est mergé, pas livré. C'est une réponse, pas un échec. */
    version: string | null;
    tag: string | null;
    tagDate: string | null;
    commitDate: string;
    reason?: string;
}

/** Dans quelle version ce commit est-il parti ? Le premier tag qui le contient. */
export async function resolveFixVersion(commitHash: string): Promise<FixVersionResolution> {
    const fn = httpsCallable<{ commitHash: string }, FixVersionResolution>(
        functions,
        'resolveFixVersion',
        {timeout: 120000},
    );
    const result = await fn({commitHash});
    return result.data;
}

export async function findFixCommits(bugId: string): Promise<FixCommitSearch> {
    const fn = httpsCallable<{ bugId: string }, FixCommitSearch>(
        functions,
        'findFixCommits',
        {timeout: 180000},
    );
    const result = await fn({bugId});
    return result.data;
}

/** Renvoie null si le bug a ete supprime depuis que le feedback y renvoie. */
export async function getBug(bugId: string): Promise<Bug | null> {
    const snap = await getDoc(doc(db, BUGS_COLLECTION, bugId));
    return snap.exists() ? ({id: snap.id, ...snap.data()} as Bug) : null;
}

export async function listBugs(status?: BugStatus): Promise<Bug[]> {
    const constraints = status
        ? [where('status', '==', status), orderBy('createdAt', 'desc'), limit(100)]
        : [orderBy('createdAt', 'desc'), limit(100)];
    const snap = await getDocs(query(collection(db, BUGS_COLLECTION), ...constraints));
    return snap.docs.map((d) => ({id: d.id, ...d.data()}) as Bug);
}

/** Les bugs deja rattaches a un feedback donne. */
export async function listBugsForFeedback(feedbackId: string): Promise<Bug[]> {
    const snap = await getDocs(query(
        collection(db, BUGS_COLLECTION),
        where('feedbackIds', 'array-contains', feedbackId),
    ));
    return snap.docs.map((d) => ({id: d.id, ...d.data()}) as Bug);
}

export async function updateBugStatus(
    bugId: string,
    status: BugStatus,
    updatedBy: string,
    fixVersion?: string,
): Promise<void> {
    await updateDoc(doc(db, BUGS_COLLECTION, bugId), {
        status,
        // Le numero de version n'a de sens qu'une fois le correctif livre.
        ...(status === 'shipped' && fixVersion ? {fixVersion} : {}),
        updatedAt: serverTimestamp(),
        updatedBy,
    });
}

/** Champs qu'un humain renseigne au fil de la vie du bug. */
export type BugEditableFields = Pick<
    Bug,
    'title' | 'fixVersion' | 'prUrl' | 'fixCommit' | 'regressionTestRef' | 'note'
>;

export async function updateBugFields(
    bugId: string,
    patch: Partial<BugEditableFields>,
    updatedBy: string,
): Promise<void> {
    // Une chaine vide efface le champ plutot que d'enregistrer du vide.
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(patch)) {
        if (typeof v === 'string' && v.trim() === '') clean[k] = deleteField();
        else if (v !== undefined) clean[k] = v;
    }
    if (Object.keys(clean).length === 0) return;
    await updateDoc(doc(db, BUGS_COLLECTION, bugId), {
        ...clean,
        updatedAt: serverTimestamp(),
        updatedBy,
    });
}
